import InvoiceDiscrepancyAction from './InvoiceDiscrepancyAction'
import ReportProductIssueButton from 'src/components/productIssue/ReportProductIssueButton'
import { PRODUCT_ISSUE_SOURCE } from 'src/utils/productIssue'
import moment from 'moment'
import {
  DeleteOutlined,
  DisconnectOutlined,
  DownloadOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  FileSearchOutlined,
  HistoryOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  SyncOutlined,
  MailOutlined,
  ThunderboltOutlined,
  UndoOutlined,
} from '@ant-design/icons'
import { Button, Popconfirm, Spin, Tag, Tooltip } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import { stripTransientProps } from 'src/utils/stripTransientProps'
import {
  archiveInvoice,
  canArchiveInvoice,
  getArchiveBlockReason,
  restoreInvoice,
  permanentlyDeleteArchivedInvoice,
  unlinkInvoiceFromPO,
} from '../../features/NewInvoicesSlice'
import { updateInvoiceStatus } from 'src/features/InvoiceSlice'
import {
  getEditInvoiceDetailsActionMeta,
  getInvoiceViewOrderPath,
  getInvoicePurchaseOrderLines,
  canQuickLinkSuggestedPo,
  formatInvoiceTotalDisplay,
  formatSupplierTitleDisplay,
  getDocumentTypeLabel,
  getDocumentTypeTagColor,
  getInvoiceLifecycleStatusDisplay,
  getInvoiceMatchingBadgeTitle,
  getInvoiceMatchingDisplayLabel,
  getInvoiceMatchingFailureReasonMeta,
  getInvoiceMatchingAttachedDisplayOverride,
  getInvoiceMatchingTagPropsForInvoice,
  getInvoiceReconciliationMethodLabel,
  getInvoiceReconciliationMethodTagProps,
  getInvoiceSourceLabel,
} from '../../utils/invoiceUploadHelpers'
import {
  canShowMatchAction,
  getInvoiceMatchActionMeta,
  getMatchModalActionPresentation,
  MATCH_ACTION_PRIMARY,
} from '../../utils/invoiceMatchActionHelpers'
import {
  getNoOrderColumnTip,
  getNoOrderGuidanceLinks,
  shouldShowNoOrderColumnDeepLinks,
  shouldShowNoOrderColumnTip,
} from '../../utils/invoiceNoOrderGuidance'
import { Link } from 'react-router-dom'
import { getEditAllocationsActionMeta } from '../../utils/invoicePoAllocationHelpers'
import { getRemoveFromPoActionMeta } from '../../utils/invoiceRemoveFromPoHelpers'
import { getUnreconcileActionMeta } from '../../utils/invoiceUnreconcileActionHelpers'
import { hasAddPagesOffer } from '../../utils/invoiceMultiDocRules'
import { getInvoiceMatchedOrdersTableDisplay } from '../../utils/invoiceMatchedOrdersDisplayHelpers'
import { canCreateMatchingManualOrder } from '../../utils/applyManualOrderProposal'

const getDaysUntilDeletion = permanentDeleteAt => {
  if (!permanentDeleteAt) return null
  return Math.max(0, moment(permanentDeleteAt).diff(moment(), 'days'))
}

function emailInvoiceConfirmTitle(invoice, forwardAddresses = []) {
  if (!forwardAddresses.length) return 'Save a forward address first.'
  const already = Array.isArray(invoice?.forwardHistory)
    ? invoice.forwardHistory.filter(Boolean)
    : []
  const latest = already[already.length - 1]
  const destination = forwardAddresses.join(', ')
  if (latest) {
    return `${latest}. This invoice was already emailed. Send it again to ${destination}?`
  }
  return `Email this invoice to ${destination}?`
}

const InvoicesColumns = (
  venueId,
  downloadInvoice,
  downloadingInvoice,
  setEditInvoice,
  loadingRowId,
  setLoadingRowId,
  dispatch,
  viewMode = 'active',
  archivedByUsername = 'Admin',
  onMatchInvoice,
  onAllocateInvoice,
  onReviewDuplicate,
  onViewAuditHistory,
  onRematchInvoice,
  rematchingInvoiceId = null,
  onDismissDuplicate,
  dismissingDuplicateInvoiceId = null,
  onUnreconcileInvoice,
  onDiscrepancyChanged,
  onCreateMatchingManualOrder,
  creatingMatchingManualOrderInvoiceId = null,
  forwardAddresses = [],
  onEmailInvoice,
  onEditGoodsReceipt,
) => {
  const isArchiveView = viewMode === 'archive'

  const baseColumns = [
    {
      title: (
        <Tooltip
          placement='top'
          title={
            <>
              <div>
                <strong>Unprocessed</strong> – uploaded and waiting to be
                matched.
              </div>
              <div>
                <strong>Match found / Perfect match</strong> – recommended order
                within tolerance; ready to review and reconcile.
              </div>
              <div>
                <strong>Partial match</strong> – covers part of the PO; more
                invoices may be expected.
              </div>
              <div>
                <strong>Review needed / No match</strong> – choose a PO
                manually.
              </div>
              <div>
                <strong>Possible duplicate</strong> – may already exist in OMS.
              </div>
            </>
          }
        >
          <MatchingColumnTitle>
            Matching{' '}
            <InfoCircleOutlined
              style={{ marginLeft: 4, fontSize: 12, color: '#8c8c8c' }}
            />
          </MatchingColumnTitle>
        </Tooltip>
      ),
      dataIndex: 'matchingStatus',
      key: 'matchingStatus',
      width: 160,
      className: 'matching-column restrict-shrink',
      render: (matchingStatus, invoice) => {
        const label = getInvoiceMatchingDisplayLabel(invoice)
        const title = getInvoiceMatchingBadgeTitle(invoice)
        const failureMeta = getInvoiceMatchingFailureReasonMeta(
          invoice.matchingFailureReason,
        )
        const attachedOverride =
          getInvoiceMatchingAttachedDisplayOverride(invoice)
        // Soften attached rows: engine failure chip stays in the tooltip only.
        const showInlineReason =
          matchingStatus === 'needs_action' &&
          Boolean(invoice.matchingFailureReason) &&
          !attachedOverride
        const showDuplicateAction =
          matchingStatus === 'possible_duplicate' &&
          invoice.matchSummary?.duplicateMatchInvoiceIds?.length > 0
        const showAddPagesAction = hasAddPagesOffer(invoice)
        const noOrderLinks = venueId
          ? getNoOrderGuidanceLinks(venueId, {
              invoiceId: invoice.id,
              purchaseOrderNumber: invoice.purchaseOrderNumber,
            })
          : null
        return (
          <MatchingCell>
            <Tooltip title={title}>
              <Tag {...getInvoiceMatchingTagPropsForInvoice(invoice).tagProps}>
                {label}
              </Tag>
            </Tooltip>
            {showInlineReason ? (
              <MatchingReason title={title}>{failureMeta.label}</MatchingReason>
            ) : null}
            <MatchingActions>
              {!isArchiveView && (
                <InvoiceDiscrepancyAction
                  compact
                  invoice={invoice}
                  venueId={venueId}
                  onChanged={onDiscrepancyChanged}
                  onEditGoodsReceipt={onEditGoodsReceipt}
                />
              )}
              {!isArchiveView && venueId ? (
                <ReportProductIssueButton
                  venueId={venueId}
                  invoice={invoice}
                  supplierTitle={
                    invoice.supplierTitle || invoice.supplier?.title || ''
                  }
                  product={{
                    title:
                      invoice.matchedProductTitle || invoice.productTitle || '',
                    sku: invoice.matchedProductSku || invoice.sku || '',
                    suppliersProductCode:
                      invoice.matchedProductCode || invoice.productCode || '',
                    price: invoice.unitPrice,
                    size: invoice.packSize || invoice.size,
                    UoM: invoice.UoM || invoice.sellingUnit,
                    hasVat: invoice.hasVat,
                    vatTreatment: invoice.vatTreatment,
                    supplier: invoice.supplier,
                  }}
                  sourceScreen={PRODUCT_ISSUE_SOURCE.INVOICES}
                  label='Report product data'
                  type='link'
                  size='small'
                />
              ) : null}
              {showAddPagesAction ? (
                <MatchingActionLink
                  type='link'
                  size='small'
                  onClick={() => onReviewDuplicate?.(invoice)}
                >
                  Add to existing
                </MatchingActionLink>
              ) : null}
              {showDuplicateAction ? (
                <MatchingActionLink
                  type='link'
                  size='small'
                  onClick={() => onReviewDuplicate?.(invoice)}
                >
                  View existing
                </MatchingActionLink>
              ) : null}
            </MatchingActions>
            {shouldShowNoOrderColumnTip(invoice) ? (
              <NoOrderTip title={getNoOrderColumnTip(invoice)}>
                {getNoOrderColumnTip(invoice)}{' '}
                {noOrderLinks && shouldShowNoOrderColumnDeepLinks(invoice) ? (
                  <>
                    <NoOrderInlineLink to={noOrderLinks.receipting}>
                      Goods receipting
                    </NoOrderInlineLink>
                    {' · '}
                    <NoOrderInlineLink to={noOrderLinks.ordersManual}>
                      Add manual order
                    </NoOrderInlineLink>
                  </>
                ) : null}
              </NoOrderTip>
            ) : null}
          </MatchingCell>
        )
      },
    },
    {
      title: (
        <Tooltip
          placement='top'
          title={
            <>
              <div>
                <strong>Received</strong> – in the library, not on a PO yet
                (mirrors GR “No invoice attached”).
              </div>
              <div>
                <strong>Invoice attached</strong> – file linked to a PO; order
                not closed yet (same wording as Goods Receipting). PO under the
                badge.
              </div>
              <div>
                <strong>Reconciled</strong> – Goods Receipting Full accept /
                saved and this invoice closed. PO under the badge.
              </div>
              <div>
                <strong>Sent to Xero</strong> – same as Goods Receipting.
              </div>
              <div>
                <strong>Error – Cannot Read</strong> – OMS could not read the
                file; try re‑uploading or check the file format.
              </div>
              <div>
                <strong>Error Sending to Xero</strong> – there was a problem
                sending this invoice to Xero.
              </div>
            </>
          }
        >
          <StatusColumnTitle>
            Status{' '}
            <InfoCircleOutlined
              style={{ marginLeft: 4, fontSize: 12, color: '#8c8c8c' }}
            />
          </StatusColumnTitle>
        </Tooltip>
      ),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      className: 'restrict-shrink',
      render: (status, invoice) => {
        const display = getInvoiceLifecycleStatusDisplay({
          ...invoice,
          status,
        })
        return (
          <StatusCell>
            <Tag {...display.tagProps}>{display.label}</Tag>
            {display.purchaseOrderNumber ? (
              <StatusPo title={display.purchaseOrderNumber}>
                {display.purchaseOrderNumber}
              </StatusPo>
            ) : null}
          </StatusCell>
        )
      },
    },
    {
      title: (
        <Tooltip
          placement='top'
          title='How this invoice was reconciled (for example Smart Match – One Click, Force, or Manual). Empty until the order is fully accepted.'
        >
          <ReconciliationMethodTitle>
            <span>Reconciliation</span>
            <span>
              method{' '}
              <InfoCircleOutlined
                style={{ marginLeft: 2, fontSize: 11, color: '#8c8c8c' }}
              />
            </span>
          </ReconciliationMethodTitle>
        </Tooltip>
      ),
      dataIndex: 'reconciliationMethod',
      key: 'reconciliation_method',
      width: 130,
      render: method =>
        method ? (
          <MethodTag {...getInvoiceReconciliationMethodTagProps(method)}>
            {getInvoiceReconciliationMethodLabel(method)}
          </MethodTag>
        ) : (
          <EmptyCell>Not reconciled yet</EmptyCell>
        ),
    },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 90,
      render: source => (
        <CompactCell title={getInvoiceSourceLabel(source)}>
          {getInvoiceSourceLabel(source)}
        </CompactCell>
      ),
    },
    {
      title: 'Uploaded',
      dataIndex: 'uploadedDate',
      key: 'uploaded',
      width: 128,
      render: (dateTime, invoice) => {
        const name = invoice.username || invoice.uploadedByName || '—'
        if (!dateTime) {
          return (
            <UploadedCell title={name}>
              <span>{name}</span>
            </UploadedCell>
          )
        }
        const when = moment(dateTime).format('DD/MM/YYYY HH:mm')
        return (
          <UploadedCell title={`${name} · ${when}`}>
            <span>{name}</span>
            <UploadedTime>{when}</UploadedTime>
          </UploadedCell>
        )
      },
    },
    {
      title: 'Downloaded',
      dataIndex: 'downloadedAt',
      key: 'downloaded',
      width: 128,
      render: (dateTime, invoice) => {
        if (!dateTime && !invoice.downloaded) {
          return <EmptyCell>—</EmptyCell>
        }
        const name = invoice.downloadedByName || '—'
        if (!dateTime) {
          return (
            <UploadedCell title={name}>
              <span>{name}</span>
            </UploadedCell>
          )
        }
        const when = moment(dateTime).format('DD/MM/YYYY HH:mm')
        return (
          <UploadedCell title={`${name} · ${when}`}>
            <span>{name}</span>
            <UploadedTime>{when}</UploadedTime>
          </UploadedCell>
        )
      },
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplier_title',
      width: 120,
      render: supplierTitle => {
        const label = formatSupplierTitleDisplay(supplierTitle)
        if (label === 'No supplier') {
          return <EmptyCell>{label}</EmptyCell>
        }
        return <SupplierCell title={label}>{label}</SupplierCell>
      },
    },
    {
      title: 'Type',
      dataIndex: 'documentType',
      key: 'document_type',
      width: 72,
      render: documentType => (
        <TypeTag color={getDocumentTypeTagColor(documentType)}>
          {getDocumentTypeLabel(documentType)}
        </TypeTag>
      ),
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNumber',
      key: 'invoice_number',
      width: 90,
      render: (value, invoice) => (
        <span>
          {value || '—'}
          {Array.isArray(invoice.forwardHistory)
            ? invoice.forwardHistory.map((line, index) => (
                <ForwardHistory key={`${line}-${index}`}>{line}</ForwardHistory>
              ))
            : null}
          {invoice.forwardNote ? (
            <ForwardNote>{invoice.forwardNote}</ForwardNote>
          ) : null}
        </span>
      ),
    },
    {
      title: 'PO',
      dataIndex: 'purchaseOrderNumber',
      key: 'purchaseOrderNumber',
      width: 120,
      render: (value, invoice) => {
        const matchedOrders = getInvoiceMatchedOrdersTableDisplay(invoice)
        const lines = getInvoicePurchaseOrderLines(invoice)
        const suggested = lines.suggested
        const showSuggestion =
          Boolean(suggested) && canQuickLinkSuggestedPo(invoice)

        return (
          <PoCell>
            <StyledPOField>
              {matchedOrders.isMultiPo ? (
                <p style={{ marginBottom: 0 }} title={matchedOrders.subtitle}>
                  {matchedOrders.label}
                  <PoMultiHint>{matchedOrders.subtitle}</PoMultiHint>
                </p>
              ) : lines.confirmed ? (
                <p style={{ marginBottom: 0 }} title='Confirmed purchase order'>
                  <PoKind>Confirmed</PoKind>
                  {lines.confirmed}
                </p>
              ) : (
                <p style={{ marginBottom: 0 }}>{lines.documentPo || '—'}</p>
              )}
            </StyledPOField>
            {showSuggestion && (
              <SuggestedPoRow>
                <SuggestedPoText title={suggested}>
                  Suggested: {suggested}
                </SuggestedPoText>
                {!isArchiveView && (
                  <Button
                    type='link'
                    size='small'
                    style={{ padding: 0, height: 'auto' }}
                    onClick={() => onMatchInvoice?.(invoice)}
                  >
                    Match
                  </Button>
                )}
              </SuggestedPoRow>
            )}
          </PoCell>
        )
      },
    },
    {
      title: 'Total',
      dataIndex: 'invoiceTotal',
      key: 'invoice_total',
      width: 210,
      align: 'right',
      render: (invoiceTotal, invoice) => {
        const catchWeight = String(
          invoice?.matchSummary?.catchWeightPriceLabel || '',
        ).trim()
        return (
          <TotalCell>
            {formatInvoiceTotalDisplay(invoiceTotal)}
            {catchWeight ? (
              <CatchWeightNote title='Receipted weight × price'>
                Receipted {catchWeight}
              </CatchWeightNote>
            ) : null}
          </TotalCell>
        )
      },
    },
    {
      title: 'Name',
      dataIndex: 'fileName',
      key: 'file_name',
      width: 140,
      ellipsis: true,
      render: (value, invoice) => {
        const name = value ? String(value).split('/').pop() : '—'
        const pageCount = Number(invoice?.pageCount) || 1
        if (pageCount <= 1) return name
        return (
          <span title={`${pageCount} pages`}>
            {name} · {pageCount} pages
          </span>
        )
      },
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
      key: 'notes',
      width: 100,
      ellipsis: true,
    },
  ]

  if (isArchiveView) {
    baseColumns.push({
      title: 'Days Until Deletion',
      dataIndex: 'permanentDeleteAt',
      key: 'permanentDeleteAt',
      width: '12%',
      render: permanentDeleteAt => {
        const days = getDaysUntilDeletion(permanentDeleteAt)
        if (days === null) return '-'
        return days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'}`
      },
    })
  }

  baseColumns.push({
    title: 'Actions',
    dataIndex: 'invoiceUrl',
    key: 'invoiceUrl',
    width: isArchiveView ? 200 : 140,
    fixed: 'right',
    render: (value, invoice) => {
      const canArchive = canArchiveInvoice(invoice)
      const removeFromPo = getRemoveFromPoActionMeta(invoice)
      const unreconcileMeta = getUnreconcileActionMeta(invoice)
      const editDetails = getEditInvoiceDetailsActionMeta(invoice)
      const blockReason = getArchiveBlockReason(invoice)
      const showCreateMatchingManualOrder =
        canCreateMatchingManualOrder(invoice)

      if (isArchiveView) {
        return (
          <ActionsBox>
            <ActionPrimaryRow>
              <Tooltip title='View audit history'>
                <ActionIconButton
                  type='default'
                  onClick={() => onViewAuditHistory?.(invoice)}
                  aria-label='View audit history'
                >
                  <HistoryOutlined />
                </ActionIconButton>
              </Tooltip>
              <Tooltip title='View invoice'>
                <ActionIconLink
                  href={`/invoice/${venueId}/${invoice.id}`}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  <EyeOutlined />
                </ActionIconLink>
              </Tooltip>
              <Tooltip title='Download'>
                <ActionIconButton
                  type='default'
                  disabled={downloadingInvoice === invoice.id}
                  onClick={() => downloadInvoice(invoice)}
                >
                  {downloadingInvoice === invoice.id ? (
                    <Spin size='small' />
                  ) : (
                    <DownloadOutlined />
                  )}
                </ActionIconButton>
              </Tooltip>
              <Popconfirm
                title='Restore this invoice to your active list?'
                placement='topRight'
                onConfirm={() => {
                  dispatch(restoreInvoice(invoice.id, venueId))
                }}
                okText='Restore'
                cancelText='Cancel'
              >
                <Tooltip title='Restore invoice'>
                  <ActionIconButton type='primary'>
                    <UndoOutlined />
                  </ActionIconButton>
                </Tooltip>
              </Popconfirm>
              <Popconfirm
                title='This invoice will be permanently deleted and cannot be restored. Are you sure?'
                placement='topRight'
                onConfirm={() => {
                  dispatch(
                    permanentlyDeleteArchivedInvoice(invoice.id, venueId),
                  )
                }}
                okText='Yes'
                cancelText='No'
              >
                <Tooltip title='Permanently delete'>
                  <ActionIconButton type='danger'>
                    <DeleteOutlined />
                  </ActionIconButton>
                </Tooltip>
              </Popconfirm>
            </ActionPrimaryRow>
          </ActionsBox>
        )
      }

      const isPossibleDuplicate =
        invoice.matchingStatus === 'possible_duplicate'
      const allocateMeta = getEditAllocationsActionMeta(invoice)
      const showAllocate = allocateMeta.canShow
      const showMatch = canShowMatchAction(invoice)
      const matchAction = showMatch ? getInvoiceMatchActionMeta(invoice) : null
      const matchPresentation = matchAction
        ? getMatchModalActionPresentation(invoice, {
            // Table CTA assumes the recommended happy path; modal re-checks
            // if the user picks a different PO.
            canMatchAndReconcile:
              matchAction.primaryAction ===
              MATCH_ACTION_PRIMARY.MATCH_AND_RECONCILE,
            selectedIsRecommended: true,
          })
        : null
      const hasPrimaryText = showAllocate || showMatch

      return (
        <ActionsBox>
          {hasPrimaryText && (
            <ActionPrimaryRow>
              {showMatch && (
                <Tooltip
                  title={
                    isPossibleDuplicate
                      ? 'Review the possible duplicate first — archive the copy you don’t want, or mark this as not a duplicate, then match.'
                      : matchPresentation?.bannerGuidance ||
                        matchAction.guidance
                  }
                >
                  <MatchActionButton
                    type='default'
                    $toneStyles={matchAction.styles}
                    loading={
                      !isPossibleDuplicate && rematchingInvoiceId === invoice.id
                    }
                    disabled={Boolean(
                      rematchingInvoiceId && rematchingInvoiceId !== invoice.id,
                    )}
                    onClick={() => onMatchInvoice?.(invoice)}
                  >
                    {isPossibleDuplicate
                      ? 'Review'
                      : matchPresentation?.tableButtonLabel || 'Match'}
                  </MatchActionButton>
                </Tooltip>
              )}

              {showAllocate && (
                <Tooltip title={allocateMeta.tooltip}>
                  <ActionTextButton
                    type='primary'
                    ghost
                    onClick={() => onAllocateInvoice?.(invoice)}
                  >
                    {allocateMeta.buttonLabel}
                  </ActionTextButton>
                </Tooltip>
              )}
            </ActionPrimaryRow>
          )}

          {showCreateMatchingManualOrder && (
            <Tooltip title='Create order from invoice — build a historical manual order from this invoice, auto-link the PO, then open Match. Nothing is sent to the supplier.'>
              <CreateMatchingManualOrderButton
                type='default'
                loading={creatingMatchingManualOrderInvoiceId === invoice.id}
                disabled={Boolean(
                  creatingMatchingManualOrderInvoiceId &&
                    creatingMatchingManualOrderInvoiceId !== invoice.id,
                )}
                onClick={() => onCreateMatchingManualOrder?.(invoice)}
                aria-label='Create order from invoice'
              >
                <ThunderboltOutlined />
                Create order
              </CreateMatchingManualOrderButton>
            </Tooltip>
          )}

          <ActionSecondaryRow>
            {invoice.matchingStatus === 'possible_duplicate' && (
              <>
                <Tooltip title='Check again — re-run matching for this invoice only'>
                  <ActionIconButton
                    type='default'
                    loading={rematchingInvoiceId === invoice.id}
                    disabled={Boolean(
                      (rematchingInvoiceId &&
                        rematchingInvoiceId !== invoice.id) ||
                        dismissingDuplicateInvoiceId,
                    )}
                    onClick={() => onRematchInvoice?.(invoice)}
                    aria-label='Check matching again'
                  >
                    <SyncOutlined />
                  </ActionIconButton>
                </Tooltip>
                <Tooltip title='Not a duplicate — mark reviewed and continue matching'>
                  <ActionIconButton
                    type='default'
                    loading={dismissingDuplicateInvoiceId === invoice.id}
                    disabled={Boolean(
                      (dismissingDuplicateInvoiceId &&
                        dismissingDuplicateInvoiceId !== invoice.id) ||
                        rematchingInvoiceId,
                    )}
                    onClick={() => onDismissDuplicate?.(invoice)}
                    aria-label='Mark as not a duplicate'
                  >
                    <CheckCircleOutlined />
                  </ActionIconButton>
                </Tooltip>
              </>
            )}

            <Tooltip title='View audit history'>
              <ActionIconButton
                type='default'
                onClick={() => onViewAuditHistory?.(invoice)}
                aria-label='View audit history'
              >
                <HistoryOutlined />
              </ActionIconButton>
            </Tooltip>

            {!isArchiveView && editDetails.canShow ? (
              <Tooltip title={editDetails.tooltip}>
                <ActionIconButton
                  type='default'
                  disabled={!editDetails.canEdit}
                  onClick={() => {
                    if (!editDetails.canEdit) return
                    setEditInvoice?.(invoice)
                  }}
                  aria-label={
                    editDetails.canEdit
                      ? 'Edit invoice details'
                      : editDetails.blockReason
                  }
                >
                  <EditOutlined />
                </ActionIconButton>
              </Tooltip>
            ) : null}

            <Tooltip title='View invoice'>
              <ActionIconLink
                href={`/invoice/${venueId}/${invoice.id}`}
                target='_blank'
                rel='noopener noreferrer'
              >
                <EyeOutlined />
              </ActionIconLink>
            </Tooltip>

            {getInvoiceViewOrderPath(venueId, invoice) ? (
              <Tooltip title='View order'>
                <ActionIconLink
                  href={getInvoiceViewOrderPath(venueId, invoice)}
                  target='_blank'
                  rel='noopener noreferrer'
                  aria-label='View order'
                >
                  <FileSearchOutlined />
                </ActionIconLink>
              </Tooltip>
            ) : null}

            <Tooltip title='Download'>
              <ActionIconButton
                type='default'
                disabled={downloadingInvoice === invoice.id}
                onClick={() => downloadInvoice(invoice)}
              >
                {downloadingInvoice === invoice.id ? (
                  <Spin size='small' />
                ) : (
                  <DownloadOutlined />
                )}
              </ActionIconButton>
            </Tooltip>

            <Popconfirm
              title={emailInvoiceConfirmTitle(invoice, forwardAddresses)}
              okText='Email invoice'
              cancelText='Cancel'
              placement='topRight'
              onConfirm={() => {
                if (!forwardAddresses.length) return
                onEmailInvoice?.(invoice)
              }}
            >
              <Tooltip title='Email invoice'>
                <ActionIconButton type='default' aria-label='Email invoice'>
                  <MailOutlined />
                </ActionIconButton>
              </Tooltip>
            </Popconfirm>

            {removeFromPo.canShow && (
              <Popconfirm
                title={removeFromPo.confirmCopy}
                placement='topRight'
                overlayStyle={{ maxWidth: 360 }}
                onConfirm={() => {
                  dispatch(
                    unlinkInvoiceFromPO(
                      invoice.id,
                      venueId,
                      archivedByUsername,
                    ),
                  )
                }}
                okText='Remove'
                cancelText='Cancel'
              >
                <Tooltip title={removeFromPo.buttonLabel}>
                  <ActionIconButton
                    type='default'
                    aria-label={removeFromPo.buttonLabel}
                  >
                    <DisconnectOutlined />
                  </ActionIconButton>
                </Tooltip>
              </Popconfirm>
            )}

            {unreconcileMeta.canShow && (
              <Tooltip title={unreconcileMeta.buttonLabel}>
                <ActionIconButton
                  type='default'
                  aria-label={unreconcileMeta.buttonLabel}
                  onClick={() => onUnreconcileInvoice?.(invoice)}
                >
                  <UndoOutlined />
                </ActionIconButton>
              </Tooltip>
            )}

            {invoice.status !== 'attachedToPO' &&
              invoice.status !== 'reconciled' &&
              invoice.status !== 'sentToXero' && (
                <Tooltip title='Mark as Error'>
                  <ActionIconButton
                    type={invoice.status === 'error' ? 'primary' : 'default'}
                    danger={invoice.status === 'error'}
                    disabled={loadingRowId === invoice.id}
                    onClick={async () => {
                      setLoadingRowId(invoice.id)
                      try {
                        const updatedStatus =
                          invoice.status === 'error' ? 'pending' : 'error'
                        await dispatch(
                          updateInvoiceStatus(
                            invoice.id,
                            venueId,
                            updatedStatus,
                          ),
                        )
                      } catch (error) {
                        console.error('Failed to update invoice status', error)
                      } finally {
                        setLoadingRowId(null)
                      }
                    }}
                  >
                    {loadingRowId === invoice.id ? (
                      <Spin size='small' />
                    ) : (
                      <ExclamationCircleOutlined />
                    )}
                  </ActionIconButton>
                </Tooltip>
              )}

            {canArchive && (
              <Popconfirm
                title='This invoice will be archived for 30 days before being permanently deleted.'
                placement='topRight'
                onConfirm={() => {
                  dispatch(
                    archiveInvoice(invoice.id, venueId, archivedByUsername),
                  )
                }}
                okText='Archive'
                cancelText='Cancel'
              >
                <Tooltip title='Archive'>
                  <ActionIconButton type='danger'>
                    <DeleteOutlined />
                  </ActionIconButton>
                </Tooltip>
              </Popconfirm>
            )}

            {!canArchive && (
              <Tooltip
                title={
                  blockReason ||
                  'This invoice is linked to a PO or already sent to Xero. Use Remove from this PO before archiving.'
                }
              >
                <span>
                  <ActionIconButton type='danger' disabled>
                    <DeleteOutlined />
                  </ActionIconButton>
                </span>
              </Tooltip>
            )}
          </ActionSecondaryRow>
        </ActionsBox>
      )
    },
  })

  return baseColumns
}

const MatchingColumnTitle = styled.span`
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
`

const MatchingCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  width: 100%;
  text-align: left;

  .ant-tag {
    margin: 0;
    font-size: 11px;
    line-height: 18px;
    padding: 0 6px;
    white-space: normal;
    max-width: 100%;
    align-self: flex-start;
  }
`

const MatchingActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
`

const MatchingReason = styled.span`
  max-width: 140px;
  color: #873800;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.3;
`

const NoOrderTip = styled.span`
  display: block;
  max-width: 160px;
  margin-top: 2px;
  color: #595959;
  font-size: 10px;
  font-weight: 500;
  line-height: 1.35;
`

const NoOrderInlineLink = styled(Link)`
  color: #cf1322;
  text-decoration: underline;
  white-space: nowrap;

  &:hover {
    color: #a8071a;
  }
`

const MatchingActionLink = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: flex-start;
    height: auto;
    min-height: 0;
    margin: 0;
    padding: 0;
    border: none;
    box-shadow: none;
    background: transparent;
    color: #595959;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.3;
    text-align: left;

    &:hover,
    &:focus {
      color: #262626;
      background: transparent;
      border: none;
      box-shadow: none;
    }
  }
`

const StatusColumnTitle = styled.span`
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
`

const ReconciliationMethodTitle = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.2;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.65);
  white-space: normal;
`

const StatusCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  max-width: 100%;

  .ant-tag {
    margin: 0;
    font-size: 11px;
    line-height: 18px;
    padding: 0 6px;
    white-space: nowrap;
  }
`

const StatusPo = styled.span`
  font-size: 11px;
  line-height: 1.3;
  color: rgba(0, 0, 0, 0.65);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const UploadedCell = styled.span`
  display: inline-flex;
  flex-direction: column;
  gap: 1px;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
`

const UploadedTime = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: #8c8c8c;
  line-height: 1.2;
`

const CompactCell = styled.span`
  display: inline-block;
  max-width: 100%;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: keep-all;
  overflow-wrap: normal;
`

const TotalCell = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  max-width: 100%;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  word-break: keep-all;
  overflow-wrap: normal;
  font-size: 12px;
  font-weight: 600;
`

const CatchWeightNote = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.65);
`

const MethodTag = styled(Tag)`
  && {
    margin: 0;
    max-width: 100%;
    white-space: normal;
    line-height: 1.25;
    font-size: 11px;
    padding: 2px 6px;
  }
`

const TypeTag = styled(Tag)`
  && {
    margin: 0;
    font-size: 11px;
    padding: 0 5px;
    line-height: 18px;
    white-space: nowrap;
  }
`

const ACTION_CONTROL_HEIGHT = 28
const ACTION_PAIR_WIDTH = 78
const ACTIONS_BOX_WIDTH = ACTION_PAIR_WIDTH + 28

const ActionsBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 10px;
  width: ${ACTIONS_BOX_WIDTH}px;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  background: #fafafa;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
`

const ActionPrimaryRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: ${ACTION_PAIR_WIDTH}px;
`

const ActionSecondaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, ${ACTION_CONTROL_HEIGHT}px);
  gap: 6px;
  width: ${ACTION_PAIR_WIDTH}px;
  justify-content: space-evenly;
  justify-items: center;
`

const ActionTextButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: ${ACTION_CONTROL_HEIGHT}px;
    width: ${ACTION_PAIR_WIDTH}px;
    min-width: ${ACTION_PAIR_WIDTH}px;
    margin: 0;
    padding: 0 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
  }
`

const CreateMatchingManualOrderButton = styled(Button)`
  && {
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    height: auto;
    min-height: ${ACTION_CONTROL_HEIGHT}px;
    width: ${ACTION_PAIR_WIDTH}px;
    min-width: ${ACTION_PAIR_WIDTH}px;
    max-width: ${ACTION_PAIR_WIDTH}px;
    margin: 0;
    padding: 4px 4px;
    font-size: 10px;
    font-weight: 600;
    line-height: 1.15;
    white-space: normal;
    text-align: center;
    color: #3f6600;
    background: #fcffe6;
    border-color: #d3f261;
    border-width: 2px;
  }

  && .anticon {
    font-size: 12px;
    line-height: 1;
  }

  &&:hover,
  &&:focus {
    color: #3f6600;
    background: #f4ffb8;
    border-color: #bae637;
  }
`

const MatchActionButton = styled(stripTransientProps(Button))`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: ${ACTION_CONTROL_HEIGHT}px;
    width: ${ACTION_PAIR_WIDTH}px;
    min-width: ${ACTION_PAIR_WIDTH}px;
    max-width: ${ACTION_PAIR_WIDTH}px;
    margin: 0;
    padding: 0 6px;
    font-size: 11px;
    font-weight: 600;
    line-height: 1;
    white-space: nowrap;
    color: ${props => props.$toneStyles?.color || '#135200'};
    background: ${props => props.$toneStyles?.background || '#f6ffed'};
    border-color: ${props => props.$toneStyles?.border || '#52c41a'};
    border-width: 2px;

    &:hover,
    &:focus {
      color: ${props => props.$toneStyles?.color || '#135200'};
      background: ${props => props.$toneStyles?.activeBackground || '#d9f7be'};
      border-color: ${props => props.$toneStyles?.activeBorder || '#389e0d'};
    }
  }
`

const ForwardHistory = styled.div`
  color: #555;
  font-size: 12px;
  line-height: 1.3;
  margin-top: 4px;
`

const ForwardNote = styled.div`
  color: #cf1322;
  font-size: 12px;
  line-height: 1.3;
  margin-top: 4px;
`

const ActionIconButton = styled(Button)`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: ${ACTION_CONTROL_HEIGHT}px;
    min-width: ${ACTION_CONTROL_HEIGHT}px;
    height: ${ACTION_CONTROL_HEIGHT}px;
    margin: 0;
    padding: 0;
    line-height: 1;
  }

  .anticon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    line-height: 0;
    font-size: 14px;
  }
`

const ActionIconLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${ACTION_CONTROL_HEIGHT}px;
  min-width: ${ACTION_CONTROL_HEIGHT}px;
  height: ${ACTION_CONTROL_HEIGHT}px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: #ffffff;
  color: rgba(0, 0, 0, 0.65);
  text-decoration: none;

  &:hover,
  &:focus {
    color: #1890ff;
    border-color: #1890ff;
  }

  .anticon {
    font-size: 14px;
  }
`

const StyledPOField = styled.div`
  margin: 3px 3px 3px 0;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  align-items: center;
  gap: 4px;
  min-width: 0;

  p {
    margin-bottom: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    word-break: keep-all;
  }
`

const PoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const PoMultiHint = styled.span`
  display: block;
  margin-top: 2px;
  font-size: 11px;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.35;
  white-space: normal;
`

const PoKind = styled.span`
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #132d07;
`

const SuggestedPoRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`

const SuggestedPoText = styled.span`
  font-size: 11px;
  color: #389e0d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const SupplierCell = styled.span`
  display: inline-block;
  max-width: 100%;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.85);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const EmptyCell = styled.span`
  color: rgba(0, 0, 0, 0.35);
  font-style: italic;
`

export default InvoicesColumns
